/**
 * Unauthenticated probe of the Entra device-code endpoint.
 *
 * Used to (1) validate that a configured app registration still exists before
 * the setup wizard reuses it, and (2) surface the raw AAD error when a
 * device-code sign-in fails without a usable code — MSAL wraps that response
 * in a generic "post_request_failed" error that hides the actionable
 * AADSTS code (e.g. AADSTS700016 "application not found" when the app
 * registration was deleted from the tenant).
 *
 * The probe issues a real (throwaway) device code when the app is healthy;
 * codes are single-use, never displayed, and expire on their own.
 */

import { request } from "undici";
import { logger } from "../utils/logger.js";

const PROBE_TIMEOUT_MS = 8_000;

export interface DeviceCodeProbeResult {
  /** The endpoint issued a user code — app registration is alive. */
  ok: boolean;
  /** AADSTS700016 — the app registration does not exist in this tenant. */
  appNotFound: boolean;
  /**
   * AADSTS50059 — AAD could not tie the client_id to any tenant. On the
   * /organizations endpoint (tenant unknown) this is what a nonexistent app
   * returns instead of 700016: no tenant anywhere knows this ID. A real but
   * single-tenant app fails differently (e.g. AADSTS50194), so this flag is
   * safe to treat as "no such app" without punishing single-tenant setups.
   */
  unresolvable: boolean;
  /** Raw AAD error_description when the endpoint returned an error. */
  errorDescription?: string;
}

/**
 * Probe the device-code endpoint for the given app.
 * Returns null when the probe itself is indeterminate (network failure,
 * proxy interference) — callers must fail open in that case.
 */
export async function probeDeviceCodeEndpoint(
  clientId: string,
  tenantId?: string | null
): Promise<DeviceCodeProbeResult | null> {
  const tenant = tenantId || "organizations";
  const url = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/devicecode`;
  try {
    const { statusCode, body } = await request(url, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ client_id: clientId, scope: "openid" }).toString(),
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    const data = (await body.json()) as {
      user_code?: string;
      error?: string;
      error_description?: string;
    };
    if (statusCode === 200 && data.user_code) {
      return { ok: true, appNotFound: false, unresolvable: false };
    }
    const description = data.error_description ?? data.error ?? `HTTP ${statusCode}`;
    return {
      ok: false,
      appNotFound: /AADSTS700016/i.test(description),
      unresolvable: /AADSTS50059/i.test(description),
      errorDescription: description,
    };
  } catch (err) {
    logger.debug(
      { err: err instanceof Error ? err.message : String(err) },
      "Device-code endpoint probe failed (indeterminate)"
    );
    return null;
  }
}
